import { get, head } from 'lodash';
import moment from 'moment';
import { CONFLICT, UNAUTHORIZED, OK } from 'http-status-codes';
import { RESET_APP, GO_BACK_PAGE } from '../page/actions';
import {
	CHAPS_ADD_PAYMENTS,
	CHAPS_ADD_SINGLE_PAYMENT,
	CHAPS_UPDATE_PAYMENT,
	CHAPS_DELETE_PAYMENT,
	CHAPS_COPY_REF_TO_ALL,
	CHAPS_COPY_DATE_TO_ALL,
	CHAPS_SET_AUTHORISERS,
	UPDATE_PAYMENT_ACCOUNT,
	UPDATE_PAYMENT_ACCOUNTS,
	CHAPS_UPDATE_PAYMENT_DETAILS,
	CREATE_NEW_CHAPS_TEMPLATE,
	CHAPS_TOGGLE_EDITING,
	INIT_CHAPS,
	CHAPS_SET_PAYMENT_TO_EDIT,
	CHAPS_UPDATE_ALL_DATES,
	CHAPS_UPDATE_ALL_FROM_ACCOUNTS,
} from './actions';
import { CHAPS_ADD_MULTIPLE_PAYMENTS } from './chapsAddMultiplePayments';
import { RESET_CHAPS_DAILY_LIMIT_AUTHORISATION } from './resetChapsDailyLimitAuthorisation';
import { RESET_PAYMENT_AUTHORISATION, CLEAR_PAYMENTS } from '../payments/actions';
import { DAILY_LIMIT_ERROR_CODE } from '../../config';
import {
	SUBMIT_CHAPS_PAYMENT_REVIEW_SUCCESS,
	SUBMIT_CHAPS_CONFIRMED_PAYMENT_SUCCESS,
	SUBMIT_CHAPS_CONFIRMED_BATCH_PAYMENTS_SUCCESS,
	SUBMIT_CHAPS_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS,
	SUBMIT_CHAPS_EDIT_PAYMENT_SUCCESS,
	SUBMIT_CHAPS_EDIT_PAYMENT_FAILURE,
} from '../api/chapsActions';
import { CHAPS_CUT_OFF_MISSED_UPDATE_PAYMENT_DATE } from '../calendar/actions';
import { SUBMIT_GET_CHAPS_RATES_SUCCESS } from '../rates/actions';

import { isPaymentTRAObligate } from '../../utils/authoriserUtils';
import { CONST_GBP_EQUIVALENT, CONST_AMOUNT_UPPERCASE } from '../../config/constants';
import {
	isConflictAndDailyLimitError,
	hasConflictError,
	hasMissedDeadlineError,
	getApiResponses,
} from '../../helpers/apiResponse';
import { getAuthorisationInfo } from '../../helpers/reducerHelper';

const initialPayments = [];

const defaultPaymentState = (payment) => ({
	toAccount: '',
	fromAccount: '',
	date: undefined,
	amount: '',
	reference: undefined,
	...payment,
});

const chapsConfirmation = (state, action) => ({
	...state,
	payments: state.payments.map((payment) => {
		const response = action.payload.apiResponses.find((item) => item.id === payment.id);
		const confirmation = {
			...response.body,
			responseCode: response.code,
			authType: get(response, 'body.verification_type', ''),
		};
		return { ...payment, confirmation };
	}),
});

const isSingleChapsPayment = (responses) => !(responses.length > 1);

const chapsHandlers = {
	[CHAPS_ADD_SINGLE_PAYMENT]: (state, action) => ({
		...state,
		payments: [...get(state, 'payments', []), ...action.payment],
	}),
	[CHAPS_ADD_PAYMENTS]: (state, action) => ({
		...state,
		payments: [...get(state, 'payments', []), ...action.payments],
	}),
	[UPDATE_PAYMENT_ACCOUNT]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			if (payment.id === action.paymentId) {
				return {
					...payment,
					toAccount: action.toAccount,
					fromAccount: action.fromAccount,
					...(get(action, 'reference') && { reference: action.reference }),
				};
			} else {
				return payment;
			}
		}),
	}),
	[UPDATE_PAYMENT_ACCOUNTS]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			const toAccount = action.toAccount ? action.toAccount : head(action.toAccounts);
			if (payment.id === action.paymentId) {
				const reference = get(
					action.toPayees.find((toPayee) => toPayee.id === toAccount),
					'reference',
					''
				);
				return {
					...payment,
					reference,
					toAccount,
					fromAccount: action.fromAccount,
				};
			}
			return payment;
		}),
	}),
	[CHAPS_UPDATE_PAYMENT]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			if (payment.id === action.paymentId) return { ...payment, [action.formKey]: action.value };
			return payment;
		}),
	}),
	[CHAPS_UPDATE_PAYMENT_DETAILS]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => (payment.id !== action.payment.id ? payment : action.payment)),
	}),
	[CHAPS_DELETE_PAYMENT]: (state, action) => ({
		...state,
		payments: state.payments.filter((payment) => payment.id !== action.paymentId),
	}),
	[SUBMIT_CHAPS_PAYMENT_REVIEW_SUCCESS]: (state, action) => {
		const userId = get(action, 'meta.userId');

		const newPayments = state.payments.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);

			const {
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
			} = getAuthorisationInfo(userId, response);

			const isTRAObligate = isPaymentTRAObligate(
				authorisation.ben_id_present,
				singleAuthoriserList,
				authorisation.status,
				userId
			);
			return {
				...payment,
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
				isTRAObligate,
			};
		});

		return {
			...state,
			payments: newPayments,
		};
	},
	[SUBMIT_GET_CHAPS_RATES_SUCCESS]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			const paymentsFromFx = get(action, 'payload.apiResponses', []).find(({ id }) => id === payment.id);

			const chargeAmounts = get(paymentsFromFx, 'body.details[0].charge_amount', []);
			const chapsChargeAmount = chargeAmounts.find((chargeAmount) => chargeAmount.type === CONST_AMOUNT_UPPERCASE);

			const matchedFees = get(chapsChargeAmount, 'value', get(chargeAmounts, '[0].value', 0));
			const matchedCurrency = get(chapsChargeAmount, 'currency', get(chargeAmounts, '[0].currency', 'GBP'));

			const ratesAmounts = get(paymentsFromFx, 'body.details[0].payment_amount', []);
			const ratesAmount = ratesAmounts.find((ratesAmount) => ratesAmount.type === CONST_GBP_EQUIVALENT);

			const paymentFxAmount = get(ratesAmount, 'value', get(ratesAmounts, '[0].value', '0.0'));
			const paymentFxCurrency = get(ratesAmount, 'currency', 'GBP');

			const peiId = get(paymentsFromFx, 'body.details[0].pei_id');
			const peiBulkId = get(paymentsFromFx, 'body.pei_bulk_id');
			const valueDate = get(paymentsFromFx, 'body.details[0].value_date');
			const paymentInstructionId = get(paymentsFromFx, 'body.details[0].payment_instruction_id');
			const paymentMethod = get(paymentsFromFx, 'body.details[0].payment_method');

			return {
				...payment,
				fees: matchedFees,
				matchedCurrency,
				peiId,
				peiBulkId,
				valueDate,
				paymentFxAmount,
				paymentFxCurrency,
				paymentInstructionId,
				paymentMethod,
			};
		}),
	}),
	[CHAPS_COPY_REF_TO_ALL]: (state = initialPayments, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			const { reference, paymentIds } = action;
			if (paymentIds.includes(payment.id)) return { ...payment, reference };
			return payment;
		}),
	}),
	[CHAPS_COPY_DATE_TO_ALL]: (state = initialPayments, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			const { date, paymentIds } = action;
			if (paymentIds.includes(payment.id)) return { ...payment, date };
			return payment;
		}),
	}),
	[SUBMIT_CHAPS_CONFIRMED_PAYMENT_SUCCESS]: (state, action) => {
		const responses = getApiResponses(action);
		if (hasConflictError(responses) && isSingleChapsPayment(responses) && hasMissedDeadlineError(responses)) {
			return { ...state, deadlineMissed: true };
		}
		return chapsConfirmation(state, action);
	},
	[SUBMIT_CHAPS_CONFIRMED_BATCH_PAYMENTS_SUCCESS]: (state, action) => {
		const responses = getApiResponses(action);
		if (hasConflictError(responses) && hasMissedDeadlineError(responses)) {
			return { ...state, deadlineMissed: true };
		}
		return chapsConfirmation(state, action);
	},
	[SUBMIT_CHAPS_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS]: (state = initialPayments, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);
			if (isConflictAndDailyLimitError(response)) {
				const authorisation = { ...get(response, 'body', {}), responseCode: get(response, 'code', 0) };
				return { ...payment, authorisation };
			}
			return payment;
		}),
	}),
	[RESET_CHAPS_DAILY_LIMIT_AUTHORISATION]: (state) => ({
		...state,
		payments: state.payments.map((payment) => {
			const code = get(payment, 'authorisation.error.code', 0);
			if (code === DAILY_LIMIT_ERROR_CODE) {
				const { authorisation, ...newPayment } = payment;
				return newPayment;
			}
			return payment;
		}),
	}),
	[CHAPS_SET_AUTHORISERS]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => {
			if (payment.id !== action.paymentId) return payment;
			return { ...payment, authorisers: action.authorisers };
		}),
	}),
	[CHAPS_SET_PAYMENT_TO_EDIT]: (state, action) => ({
		...state,
		payments: get(action, 'payment', []),
		selectedId: get(action, 'payment.id', ''),
	}),
	[CHAPS_TOGGLE_EDITING]: (state) => ({
		...state,
		editing: true,
	}),
	[SUBMIT_CHAPS_EDIT_PAYMENT_SUCCESS]: (state, action) => {
		const payments = state.payments.map((payment) => {
			const response = get(action, 'payload', '');
			const confirmation = {
				...response,
				responseCode: OK,
			};
			return { ...payment, confirmation };
		});
		return { ...state, payments };
	},
	[SUBMIT_CHAPS_EDIT_PAYMENT_FAILURE]: (state, action) => {
		if (get(action, 'payload.status', '') === UNAUTHORIZED) {
			const payments = state.payments.map((payment) => {
				const confirmation = {
					responseCode: UNAUTHORIZED,
					authType: get(action, 'payload.response.verification_type', ''),
				};
				return { ...payment, confirmation };
			});
			return { ...state, payments };
		}
		if (get(action, 'payload.status', '') === CONFLICT) return { ...state, deadlineMissed: true };
		return state;
	},
	[GO_BACK_PAGE]: (state) => ({
		...state,
		deadlineMissed: false,
	}),
	[CHAPS_CUT_OFF_MISSED_UPDATE_PAYMENT_DATE]: (state, action) => {
		const newDate = moment(action.nextAvailableDate);
		const payments = state.payments.map((payment) => {
			if (payment.date.isBefore(newDate)) return { ...payment, date: newDate };
			return payment;
		});
		return { ...state, payments };
	},
	[RESET_APP]: () => initialPayments,
	[CLEAR_PAYMENTS]: () => initialPayments,
	[CREATE_NEW_CHAPS_TEMPLATE]: (state, action) => ({
		payments: [defaultPaymentState(action.payment)],
	}),
	[RESET_PAYMENT_AUTHORISATION]: (state, action) => {
		if (state && !state.payments) return state;

		return {
			...state,
			payments: state.payments.map((payment) => {
				if (payment.id !== action.id) return payment;
				const { authorisation, ...newPayment } = payment;
				return newPayment;
			}),
		};
	},
	[INIT_CHAPS]: (state, action) => ({
		payments: action.payments,
	}),
	[CHAPS_UPDATE_ALL_DATES]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => ({ ...payment, date: action.date })),
	}),
	[CHAPS_UPDATE_ALL_FROM_ACCOUNTS]: (state, action) => ({
		...state,
		payments: state.payments.map((payment) => ({ ...payment, fromAccount: action.fromAccountId })),
	}),
	[CHAPS_ADD_MULTIPLE_PAYMENTS]: (state, action) => ({
		...state,
		payments: action.payments.map(defaultPaymentState),
	}),
};

const chapsPayment = (state = defaultPaymentState(), action) =>
	(chapsHandlers[action.type] || ((s) => s))(state, action);

const chaps = (state = initialPayments, action) => {
	if (Object.keys(chapsHandlers).includes(action.type)) {
		return chapsPayment(state, action);
	}

	return state;
};

export default chaps;
